import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Briefcase, Target, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DEPARTMENTS = [
  "Executive Office", "Consulting Division", "Government Relations",
  "Investor Relations", "Research & Intelligence", "Marketing & Visibility",
  "AgriSat Strategic Unit", "Legal & Compliance",
];
const STATUSES = ["planning", "active", "on_hold", "completed", "archived"];
const TASK_STATUSES = ["todo", "in_progress", "review", "done"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const PRIO_COLOR = { low: "#94A3B8", medium: "#3B82F6", high: "#F59E0B", critical: "#EF4444" };
const STATUS_COLOR = { planning: "#94A3B8", active: "#22C55E", on_hold: "#F59E0B", completed: "#3B82F6", archived: "#475569" };

const fmtMoney = (n) => {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

export default function Operations() {
  const [projects, setProjects] = useState([]);
  const [activeProj, setActiveProj] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [openProj, setOpenProj] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [pForm, setPForm] = useState({});
  const [tForm, setTForm] = useState({});

  const refresh = async () => {
    const r = await api.get("/projects");
    setProjects(r.data);
    if (!activeProj && r.data[0]) setActiveProj(r.data[0]);
  };
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (activeProj) {
      api.get(`/tasks?project_id=${activeProj.id}`).then((r) => setTasks(r.data));
    }
  }, [activeProj]);

  const taskGroups = useMemo(() => {
    const g = {}; TASK_STATUSES.forEach((s) => (g[s] = []));
    tasks.forEach((t) => g[t.status]?.push(t));
    return g;
  }, [tasks]);

  const saveProject = async () => {
    if (!pForm.name) { toast.error("Name is required"); return; }
    try {
      await api.post("/projects", pForm);
      toast.success("Project created");
      setOpenProj(false); setPForm({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveTask = async () => {
    if (!tForm.title) { toast.error("Title is required"); return; }
    try {
      await api.post("/tasks", { ...tForm, project_id: activeProj.id });
      const r = await api.get(`/tasks?project_id=${activeProj.id}`);
      setTasks(r.data);
      setOpenTask(false); setTForm({}); toast.success("Task added");
    } catch (e) { toast.error("Failed"); }
  };

  const updateTaskStatus = async (id, status) => {
    await api.put(`/tasks/${id}`, { status });
    const r = await api.get(`/tasks?project_id=${activeProj.id}`);
    setTasks(r.data);
  };

  const deleteTask = async (id) => {
    await api.delete(`/tasks/${id}`);
    const r = await api.get(`/tasks?project_id=${activeProj.id}`);
    setTasks(r.data);
  };

  return (
    <div className="p-8 space-y-6" data-testid="operations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Operations // The Operating System</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Mandates & Workstreams</h1>
          <p className="text-slate-400 mt-2 text-sm">Engagements, deliverables and execution.</p>
        </div>
        <button
          onClick={() => { setPForm({ status: "planning", priority: "medium", department: "Consulting Division" }); setOpenProj(true); }}
          data-testid="add-project-btn"
          className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          <Plus className="w-4 h-4" /> New Mandate
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Project list */}
        <div className="col-span-12 lg:col-span-4 space-y-2">
          <div className="label-eyebrow mb-2">// Mandates ({projects.length})</div>
          {projects.map((p) => (
            <motion.div
              layout
              key={p.id}
              onClick={() => setActiveProj(p)}
              data-testid={`project-${p.id}`}
              className={`p-4 cursor-pointer transition-all border ${activeProj?.id === p.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5" style={{ background: STATUS_COLOR[p.status] }}></span>
                <span className="label-eyebrow">{p.status.replace("_", " ")}</span>
                <ChevronRight className="w-3.5 h-3.5 ml-auto text-slate-600" />
              </div>
              <div className="font-display text-base text-white">{p.name}</div>
              <div className="text-xs text-slate-500 mt-1">{p.department}</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] px-1.5 py-0.5 border" style={{ borderColor: PRIO_COLOR[p.priority], color: PRIO_COLOR[p.priority] }}>
                    {p.priority.toUpperCase()}
                  </span>
                  <span className="font-mono text-xs" style={{ color: "var(--gold-400)" }}>{fmtMoney(p.budget)}</span>
                </div>
                <div className="font-mono text-xs text-white">{p.progress}%</div>
              </div>
              <div className="h-1 bg-[#10182B] mt-2 overflow-hidden">
                <div className="h-full" style={{ width: `${p.progress}%`, background: "var(--gold-500)" }}></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Active project + tasks */}
        <div className="col-span-12 lg:col-span-8">
          {activeProj ? (
            <div className="space-y-4">
              <div className="nexus-card p-6">
                <div className="label-eyebrow mb-2">// {activeProj.department}</div>
                <h2 className="font-display text-2xl text-white">{activeProj.name}</h2>
                <p className="text-slate-400 text-sm mt-2 max-w-3xl">{activeProj.description}</p>
                <div className="mt-5 grid grid-cols-4 gap-4 pt-4 border-t border-white/5">
                  <div>
                    <div className="label-eyebrow mb-1">Budget</div>
                    <div className="font-mono text-lg text-white">{fmtMoney(activeProj.budget)}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">Priority</div>
                    <div className="font-mono text-lg" style={{ color: PRIO_COLOR[activeProj.priority] }}>{activeProj.priority}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">Status</div>
                    <div className="font-mono text-lg" style={{ color: STATUS_COLOR[activeProj.status] }}>{activeProj.status}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">Progress</div>
                    <div className="font-mono text-lg text-white">{activeProj.progress}%</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="label-eyebrow">// Tasks ({tasks.length})</div>
                <button
                  onClick={() => { setTForm({ status: "todo", priority: "medium" }); setOpenTask(true); }}
                  data-testid="add-task-btn"
                  className="px-3 py-1.5 text-xs font-medium border border-white/10 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] text-slate-300 flex items-center gap-1.5"
                >
                  <Plus className="w-3 h-3" /> Add task
                </button>
              </div>

              <div className="grid grid-cols-4 gap-3">
                {TASK_STATUSES.map((s) => (
                  <div key={s} data-testid={`task-col-${s}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="label-eyebrow">{s.replace("_", " ")}</span>
                      <span className="font-mono text-xs text-slate-500">{taskGroups[s].length}</span>
                    </div>
                    <div className="space-y-2 min-h-[100px]">
                      {taskGroups[s].map((t) => (
                        <div key={t.id} className="nexus-card p-3 group">
                          <div className="text-sm text-white">{t.title}</div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: PRIO_COLOR[t.priority] }}>{t.priority}</span>
                            <button onClick={() => deleteTask(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {TASK_STATUSES.filter((x) => x !== t.status).map((target) => (
                              <button
                                key={target}
                                onClick={() => updateTaskStatus(t.id, target)}
                                className="text-[9px] font-mono px-1.5 py-0.5 border border-white/10 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] text-slate-500"
                              >→{target.slice(0, 3)}</button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="nexus-card p-10 text-center text-slate-500">Select a mandate from the left.</div>
          )}
        </div>
      </div>

      {/* Project form */}
      {openProj && (
        <Modal title="New Mandate" onClose={() => setOpenProj(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name *" col-span={2}>
              <input
                data-testid="proj-name"
                value={pForm.name || ""}
                onChange={(e) => setPForm({ ...pForm, name: e.target.value })}
                className="form-input"
              />
            </Field>
            <Field label="Department" full>
              <select value={pForm.department || ""} onChange={(e) => setPForm({ ...pForm, department: e.target.value })} className="form-input">
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select value={pForm.priority || "medium"} onChange={(e) => setPForm({ ...pForm, priority: e.target.value })} className="form-input">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={pForm.status || "planning"} onChange={(e) => setPForm({ ...pForm, status: e.target.value })} className="form-input">
                {STATUSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Budget ($)">
              <input type="number" value={pForm.budget || ""} onChange={(e) => setPForm({ ...pForm, budget: +e.target.value })} className="form-input" />
            </Field>
            <Field label="Description" full>
              <textarea rows={3} value={pForm.description || ""} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} className="form-input resize-none" />
            </Field>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setOpenProj(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
            <button onClick={saveProject} data-testid="save-project-btn" className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Create</button>
          </div>
        </Modal>
      )}

      {openTask && (
        <Modal title="New Task" onClose={() => setOpenTask(false)}>
          <div className="space-y-4">
            <Field label="Title *" full>
              <input data-testid="task-title" value={tForm.title || ""} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} className="form-input" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Status">
                <select value={tForm.status || "todo"} onChange={(e) => setTForm({ ...tForm, status: e.target.value })} className="form-input">
                  {TASK_STATUSES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <select value={tForm.priority || "medium"} onChange={(e) => setTForm({ ...tForm, priority: e.target.value })} className="form-input">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description" full>
              <textarea rows={3} value={tForm.description || ""} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} className="form-input resize-none" />
            </Field>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setOpenTask(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
            <button onClick={saveTask} data-testid="save-task-btn" className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Add Task</button>
          </div>
        </Modal>
      )}

      <style>{`.form-input { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family: 'IBM Plex Mono', monospace; font-size: 0.875rem; outline: none; }
      .form-input:focus { border-color: #D4AF37; }`}</style>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-1">// Form</div>
          <h2 className="font-display text-2xl text-white">{title}</h2>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </motion.div>
  </div>
);
